/**
 * Staff Dialog
 * Modal for adding/editing staff members
 */
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Loader2 } from "lucide-react";
import { STAFF_ROLES } from "./constants";

export const StaffDialog = ({
  open,
  onOpenChange,
  staffForm,
  setStaffForm,
  editMode,
  saving,
  onSave
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editMode ? "Edit Staff Member" : "Add Staff Member"}</DialogTitle>
          <DialogDescription>Add team managers, assistants, and volunteers</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>First Name *</Label>
              <Input 
                value={staffForm.first_name} 
                onChange={(e) => setStaffForm({ ...staffForm, first_name: e.target.value })} 
                className="mt-1" 
              />
            </div>
            <div>
              <Label>Last Name *</Label>
              <Input 
                value={staffForm.last_name} 
                onChange={(e) => setStaffForm({ ...staffForm, last_name: e.target.value })} 
                className="mt-1" 
              />
            </div>
          </div>
          <div>
            <Label>Email *</Label>
            <Input 
              type="email" 
              value={staffForm.email} 
              onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })} 
              className="mt-1" 
            />
          </div>
          <div>
            <Label>Phone</Label>
            <Input 
              type="tel" 
              value={staffForm.phone} 
              onChange={(e) => setStaffForm({ ...staffForm, phone: e.target.value })} 
              className="mt-1" 
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Role</Label>
              <Select 
                value={staffForm.role} 
                onValueChange={(value) => setStaffForm({ 
                  ...staffForm, 
                  role: value, 
                  permissions: STAFF_ROLES.find(r => r.value === value)?.permissions || "limited" 
                })}
              >
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STAFF_ROLES.map(role => (
                    <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Permissions</Label>
              <Select 
                value={staffForm.permissions} 
                onValueChange={(value) => setStaffForm({ ...staffForm, permissions: value })}
              >
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">Full Access</SelectItem>
                  <SelectItem value="limited">Limited Access</SelectItem>
                  <SelectItem value="view_only">View Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSave} disabled={saving} className="bg-slate-900">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {editMode ? "Update" : "Add Staff"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StaffDialog;
